import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  listDetalleMesas, 
  listVentasCocina, 
  createVentaMesa, 
  updateVentaMesa 
} from "../api/ventasCocina";
import { showAlertError, showAlertExito, showConfirmAlert } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiLayout, FiPlus, FiUser, FiZap, 
  FiCheckCircle, FiClock, FiSettings, FiTrash2, FiEdit3
} from "react-icons/fi";

const Mesas = () => {
  const [loading, setLoading] = useState(true);
  const [mesas, setMesas] = useState([]);
  const [ventasActivas, setVentasActivas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMesaId, setCurrentMesaId] = useState(null);
  const [form, setForm] = useState({ numero_mesa: "", cantidad_personas: "", status: 1, id_sucursal: null });
  
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resMesas, resVentas] = await Promise.all([
        listDetalleMesas(),
        listVentasCocina()
      ]);
      setMesas(resMesas?.data || []);
      setVentasActivas(resVentas?.data || []);
    } catch (error) {
      console.error("Error al cargar datos de mesas:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (mesa = null) => {
    if (mesa) {
      setForm({ 
        numero_mesa: mesa.numero_mesa, 
        cantidad_personas: mesa.cantidad_personas,
        status: mesa.status,
        id_sucursal: mesa.id_sucursal
      });
      setCurrentMesaId(mesa.id);
      setEditMode(true);
    } else {
      setForm({ numero_mesa: "", cantidad_personas: "", status: 1, id_sucursal: null });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await updateVentaMesa(currentMesaId, form);
        showAlertExito("Éxito", "Mesa actualizada correctamente.");
      } else {
        await createVentaMesa(form);
        showAlertExito("Éxito", "Mesa creada correctamente.");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showAlertError("Error", "No se pudo guardar la mesa.");
    }
  };

  const handleTomarPedido = (mesa) => {
    navigate(`/dashboard/ventas-cocina/${mesa.id}`);
  };

  if (loading && mesas.length === 0) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden animate-fade-in">
      
      {/* HEADER DE ADMINISTRACIÓN */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between shrink-0 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-600 flex items-center justify-center text-white shadow-xl shadow-orange-100">
            <FiLayout size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 leading-none tracking-tight">Gestión de Mesas</h2>
            <p className="text-sm text-slate-400 mt-1 font-medium">Administración y monitoreo en tiempo real</p>
          </div>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-2xl hover:bg-orange-700 transition-all font-bold shadow-lg shadow-orange-200 active:scale-95"
        >
          <FiPlus size={20} /> Nueva Mesa
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        
        {/* RESUMEN DE ESTADOS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <FiCheckCircle size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Disponibles</p>
                <h4 className="text-3xl font-black text-slate-800">{mesas.filter(m => m.status === 1).length}</h4>
              </div>
           </div>
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <FiClock size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Ocupadas</p>
                <h4 className="text-3xl font-black text-slate-800">{mesas.filter(m => m.status === 2).length}</h4>
              </div>
           </div>
           <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <FiUser size={28} />
              </div>
              <div>
                <p className="text-xs font-black uppercase text-slate-400 tracking-widest mb-1">Total Mesas</p>
                <h4 className="text-3xl font-black text-slate-800">{mesas.length}</h4>
              </div>
           </div>
        </div>

        {/* TABLERO DE MESAS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
          {mesas.map((m) => {
            const venta = ventasActivas.find(v => v.id_mesa === m.id && v.status !== 3);
            const isOccupied = m.status === 2 || !!venta;
            return (
              <div
                key={m.id}
                className={`group relative perspective bg-white rounded-[2.5rem] p-6 border-2 transition-all duration-500 hover:shadow-2xl flex flex-col items-center justify-center
                  ${isOccupied 
                    ? "border-rose-100 shadow-rose-50/50" 
                    : "border-emerald-100 shadow-emerald-50/50"
                  }`}
              >
                {/* Indicador de Estado Flotante */}
                <div className={`absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter shadow-sm
                  ${isOccupied ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${isOccupied ? "bg-rose-500 animate-pulse" : "bg-emerald-500"}`}></div>
                  {isOccupied ? "Ocupada" : "Libre"}
                </div>

                {/* Acciones de Edición */}
                <button 
                  onClick={() => handleOpenModal(m)}
                  className="absolute top-4 right-4 p-2 rounded-xl bg-slate-50 text-slate-300 hover:bg-orange-50 hover:text-orange-600 transition-all opacity-0 group-hover:opacity-100"
                >
                  <FiEdit3 size={14} />
                </button>

                <div className={`w-18 h-18 rounded-3xl flex items-center justify-center mb-4 transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-6
                  ${isOccupied ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}
                >
                  <span className="text-3xl font-black italic">M{m.numero_mesa}</span>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-slate-400">
                    <FiUser size={12} /> {m.cantidad_personas} Personas
                  </div>
                </div>

                {/* BOTÓN DE ACCIÓN DINÁMICO */}
                <button
                  onClick={() => handleTomarPedido(m)}
                  className={`w-full py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95
                    ${isOccupied 
                      ? "bg-rose-600 text-white shadow-rose-100 hover:bg-rose-700" 
                      : "bg-emerald-600 text-white shadow-emerald-100 hover:bg-emerald-700"
                    }`}
                >
                  {isOccupied ? "Ver Orden" : "Tomar Pedido"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* MODAL ADMIN MESAS */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoom-in">
              <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-orange-600 text-white">
                 <h4 className="font-extrabold text-lg flex items-center gap-2">
                   {editMode ? <FiEdit3 /> : <FiPlus />}
                   {editMode ? `Editar Mesa ${form.numero_mesa}` : "Nueva Mesa"}
                 </h4>
                 <button onClick={() => setShowModal(false)} className="hover:rotate-90 transition-transform"><FiPlus className="rotate-45" size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                 <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Número de Mesa</label>
                    <input 
                      type="number" 
                      required
                      value={form.numero_mesa}
                      onChange={(e) => setForm({...form, numero_mesa: e.target.value})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-orange-400 outline-none transition-all font-bold"
                    />
                 </div>
                 <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Capacidad</label>
                    <input 
                      type="number" 
                      required
                      value={form.cantidad_personas}
                      onChange={(e) => setForm({...form, cantidad_personas: e.target.value})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-orange-400 outline-none transition-all font-bold"
                    />
                 </div>
                 
                 {editMode && (
                   <div>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Estado Manual</label>
                    <select 
                      value={form.status}
                      onChange={(e) => setForm({...form, status: Number(e.target.value)})}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-orange-400 outline-none transition-all font-bold"
                    >
                      <option value={1}>Disponible</option>
                      <option value={2}>Utilizada</option>
                      <option value={3}>Reservada</option>
                    </select>
                   </div>
                 )}

                 <button type="submit" className="w-full bg-orange-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-orange-100 hover:bg-orange-700 transition-all mt-4">
                    {editMode ? "Actualizar Mesa" : "Registrar Mesa"}
                 </button>
              </form>
           </div>
        </div>
      )}

    </div>
  );
};

export default Mesas;
